import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Settings2 } from "lucide-react";

/**
 * FX3 B1 — Admin-editable Direct Partner order minimums.
 * ₹50,000 on a DP's first order, ₹30,000 on every later one, 100% advance,
 * whole master cartons. A breach does not block the order; it raises an
 * exception that routes Agent → Manager → Admin → Finance.
 */
export default function DPOrderRulesDialog() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/dp/order-rules")
      .then(r => { setSettings(r.data?.settings || []); setCanEdit(!!r.data?.can_edit); })
      .catch(e => toast.error(e.response?.data?.detail || "Could not load DP order rules"));
  };

  useEffect(() => { if (open) load(); }, [open]);

  const setValue = (key, value) =>
    setSettings(prev => prev.map(s => (s.key === key ? { ...s, value } : s)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      settings.forEach(s => { payload[s.key] = s.value; });
      await api.put("/dp/order-rules", payload);
      toast.success("DP order rules saved");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="dp-order-rules-btn">
        <Settings2 className="w-3.5 h-3.5 mr-1" /> Order Rules
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="dp-order-rules-dialog">
          <DialogHeader><DialogTitle className="font-heading text-base">Direct Partner order rules</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500">
            An order that breaks one of these rules is not blocked — it is flagged and sent
            through Manager, Admin and Finance approval.
          </p>
          <div className="space-y-3 mt-2">
            {settings.map(s => (
              <div key={s.key} className="flex items-center justify-between gap-3">
                <Label className="text-xs text-zinc-700">{s.label}</Label>
                {s.type === "boolean" ? (
                  <Switch
                    checked={!!s.value}
                    disabled={!canEdit}
                    onCheckedChange={v => setValue(s.key, v)}
                    data-testid={`dp-rule-${s.key}`}
                  />
                ) : (
                  <Input
                    type="number"
                    className="h-8 w-32 text-xs text-right"
                    value={s.value}
                    disabled={!canEdit}
                    onChange={e => setValue(s.key, e.target.value)}
                    data-testid={`dp-rule-${s.key}`}
                  />
                )}
              </div>
            ))}
          </div>
          {canEdit ? (
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setOpen(false)}>Cancel</Button>
              <Button size="sm" className="text-xs" onClick={save} disabled={saving} data-testid="dp-rules-save-btn">
                {saving ? "Saving..." : "Save rules"}
              </Button>
            </div>
          ) : (
            <p className="text-[11px] text-zinc-400 mt-3">You do not have permission to change these — dp.settings required.</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
